import React from 'react';

/**
 * The little Markdown the AI assistant and scenarios write (`SiteChatMessage.markdown`): paragraphs, bullet and numbered
 * lists, headings, **bold**, *italic*, `code` and [links](https://…). Built as React nodes — a message never becomes
 * HTML, so nothing a sender writes can inject markup into the host page.
 */
export function MarkdownText({ text, linkify, safeUrl }: {
    text: string;
    /** Plain-text links inside a run of text. */
    linkify: (text: string) => React.ReactNode[];
    safeUrl: (url: string) => string;
}) {
    const blocks: React.ReactNode[] = [];
    let list: { ordered: boolean; items: string[] } | null = null;
    let paragraph: string[] = [];

    const flushParagraph = () => {
        if (paragraph.length)
            blocks.push(<p key={blocks.length}>{paragraph.map((line, i) => <React.Fragment key={i}>{i ? <br /> : null}{inline(line, linkify, safeUrl)}</React.Fragment>)}</p>);
        paragraph = [];
    };
    const flushList = () => {
        if (list) {
            const items = list.items.map((item, i) => <li key={i}>{inline(item, linkify, safeUrl)}</li>);
            blocks.push(list.ordered ? <ol key={blocks.length}>{items}</ol> : <ul key={blocks.length}>{items}</ul>);
        }
        list = null;
    };

    for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
        const line = raw.trimEnd();
        const bullet = /^\s*[-*•]\s+(.*)$/.exec(line);
        const numbered = /^\s*\d+[.)]\s+(.*)$/.exec(line);
        const heading = /^\s*#{1,6}\s+(.*)$/.exec(line);
        if (bullet || numbered) {
            flushParagraph();
            const ordered = !!numbered;
            if (!list || list.ordered !== ordered) {
                flushList();
                list = { ordered, items: [] };
            }
            list.items.push((bullet ?? numbered)![1]);
        } else if (!line.trim()) {
            flushParagraph();
            flushList();
        } else if (heading) {
            flushParagraph();
            flushList();
            blocks.push(<p key={blocks.length}><strong>{inline(heading[1], linkify, safeUrl)}</strong></p>);
        } else {
            flushList();
            paragraph.push(line);
        }
    }
    flushParagraph();
    flushList();
    return <div className="axl-sc-md">{blocks}</div>;
}

const INLINE = /(\*\*|__)(.+?)\1|(\*|_)(?!\s)(.+?)(?<!\s)\3|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)/g;

function inline(text: string, linkify: (text: string) => React.ReactNode[], safeUrl: (url: string) => string): React.ReactNode[] {
    const out: React.ReactNode[] = [];
    let last = 0;
    let key = 0;
    for (const match of text.matchAll(INLINE)) {
        const at = match.index ?? 0;
        if (at > last)
            out.push(...linkify(text.slice(last, at)));
        if (match[2] != null)
            out.push(<strong key={`b${key++}`}>{inline(match[2], linkify, safeUrl)}</strong>);
        else if (match[4] != null)
            out.push(<em key={`i${key++}`}>{inline(match[4], linkify, safeUrl)}</em>);
        else if (match[5] != null)
            out.push(<code key={`c${key++}`}>{match[5]}</code>);
        else
            out.push(<a key={`a${key++}`} href={safeUrl(match[7])} target="_blank" rel="noopener noreferrer">{match[6]}</a>);
        last = at + match[0].length;
    }
    if (last < text.length)
        out.push(...linkify(text.slice(last)));
    return out;
}
